import { useQuery } from '@tanstack/react-query';
import { searchCities, searchCitiesByCountry, type CityOption } from '../api/geography.api';
import type { CityScope } from '@/lib/types/geography.types';
import { GEOGRAPHY_KEYS } from './geography.keys';

/** Query key + fetcher per scope kind, so the hook never branches on `kind`. */
const CITY_SEARCH_BY_SCOPE: Record<
  CityScope['kind'],
  {
    queryKey: (id: number, search: string) => readonly unknown[];
    fetch: (id: number, search: string) => Promise<CityOption[]>;
  }
> = {
  state: { queryKey: GEOGRAPHY_KEYS.cities, fetch: (id, search) => searchCities(id, search) },
  country: {
    queryKey: GEOGRAPHY_KEYS.citiesByCountry,
    fetch: (id, search) => searchCitiesByCountry(id, search),
  },
};

/**
 * Debounced-by-the-caller city search within a US state or, for non-US
 * headquarters, a whole country (MYN-46, UX sheet r105). Not prefetched -
 * City is the one tier where volume forces a live, server-filtered query.
 * React Query caches per `(scope, search)` key, so re-typing a previous prefix
 * is instant. Disabled while `scope` is absent.
 */
export function useCitySearch(scope: CityScope | undefined, search: string) {
  const config = CITY_SEARCH_BY_SCOPE[scope?.kind ?? 'state'];
  const id = scope?.id ?? 0;
  return useQuery({
    queryKey: config.queryKey(id, search),
    queryFn: () => config.fetch(id, search),
    enabled: scope !== undefined,
  });
}
